import type { RecoveryAnchor } from '@/types'
import type { IRecoveryAnchorRepository } from '@/repository/recoveryAnchorTypes'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from './schema'

type AnchorRow = Database['public']['Tables']['recovery_anchors']['Row']

/**
 * Cloud-backed recovery-anchor repository.
 *
 * The table uses `workspace_id` as the primary key — there's at most one
 * anchor per workspace — so `set()` is a `.upsert({ onConflict:
 * 'workspace_id' })` and `clear()` is a delete. Matches the singleton
 * contract exposed by `IRecoveryAnchorRepository`.
 */
export class SupabaseRecoveryAnchorRepo implements IRecoveryAnchorRepository {
  private readonly client: SupabaseClient<Database>
  private readonly userId: string
  private readonly workspaceId: string

  constructor(client: SupabaseClient<Database>, userId: string, workspaceId: string) {
    this.client = client
    this.userId = userId
    this.workspaceId = workspaceId
  }

  async get(): Promise<RecoveryAnchor | null> {
    const { data, error } = await this.client
      .from('recovery_anchors')
      .select('*')
      .eq('workspace_id', this.workspaceId)
      .maybeSingle()

    if (error) throw new Error(`[recovery_anchors.get] ${error.message}`)
    return data ? rowToAnchor(data) : null
  }

  async set(anchor: RecoveryAnchor): Promise<void> {
    const { error } = await this.client.from('recovery_anchors').upsert(
      {
        workspace_id: this.workspaceId,
        user_id: this.userId,
        start_date: anchor.startDate,
        starting_total: anchor.startingTotal,
        set_at: anchor.setAt,
      },
      { onConflict: 'workspace_id' }
    )

    if (error) throw new Error(`[recovery_anchors.set] ${error.message}`)
  }

  async clear(): Promise<void> {
    const { error } = await this.client
      .from('recovery_anchors')
      .delete()
      .eq('workspace_id', this.workspaceId)

    if (error) throw new Error(`[recovery_anchors.clear] ${error.message}`)
  }
}

function rowToAnchor(row: AnchorRow): RecoveryAnchor {
  return {
    workspaceId: row.workspace_id,
    startDate: row.start_date,
    startingTotal: Number(row.starting_total),
    setAt: row.set_at,
  }
}
